import {
  createChart,
  IChartApi,
  ISeriesApi,
  CandlestickData,
  LineData,
  Time,
  ColorType,
  CandlestickSeries,
  LineSeries,
  CandlestickSeriesOptions,
  LineSeriesOptions,
  LineType
} from 'lightweight-charts';
import { OHLCData } from './types.js';

export interface LightweightChartOptions {
  container: HTMLElement;
  width?: number;
  height?: number;
  bullishColor?: string;
  bearishColor?: string;
  backgroundColor?: string;
  textColor?: string;
  gridColor?: string;
}

export class LightweightChart {
  private chart: IChartApi;
  private candlestickSeries: ISeriesApi<'Candlestick'>;
  private lineSeries: Map<string, ISeriesApi<'Line'>> = new Map();
  private container: HTMLElement;

  constructor(options: LightweightChartOptions) {
    this.container = options.container;

    // Create chart with TradingView Lightweight Charts
    this.chart = createChart(this.container, {
      width: options.width || this.container.clientWidth,
      height: options.height || this.container.clientHeight,
      layout: {
        background: {
          type: ColorType.Solid,
          color: options.backgroundColor || '#000000'
        },
        textColor: options.textColor || '#8e8e93',
      },
      grid: {
        vertLines: {
          color: options.gridColor || 'rgba(28, 28, 30, 0.55)'
        },
        horzLines: {
          color: options.gridColor || 'rgba(28, 28, 30, 0.55)'
        },
      },
      crosshair: {
        mode: 1, // Normal crosshair mode
        vertLine: {
          width: 1,
          color: '#636366',
          style: 2, // Dashed line
        },
        horzLine: {
          width: 1,
          color: '#636366',
          style: 2, // Dashed line
        },
      },
      rightPriceScale: {
        borderColor: '#1c1c1e',
        visible: true,
      },
      timeScale: {
        borderColor: '#1c1c1e',
        timeVisible: true,
        secondsVisible: false,
      },
      handleScroll: {
        mouseWheel: true,
        pressedMouseMove: true,
        horzTouchDrag: true,
        vertTouchDrag: true,
      },
      handleScale: {
        axisPressedMouseMove: true,
        mouseWheel: true,
        pinch: true,
      },
    });

    // Add candlestick series
    const seriesOptions: Partial<CandlestickSeriesOptions> = {
      upColor: options.bullishColor || '#00c805',
      downColor: options.bearishColor || '#ff5000',
      borderVisible: false,
      wickUpColor: options.bullishColor || '#00c805',
      wickDownColor: options.bearishColor || '#ff5000',
    };

    this.candlestickSeries = this.chart.addSeries(CandlestickSeries, seriesOptions);

    // Handle window resize
    this.setupResizeHandler();
  }

  /**
   * Set data for the candlestick chart
   */
  setData(data: OHLCData[]): void {
    const formattedData: CandlestickData[] = data.map(d => ({
      time: Math.floor(d.timestamp / 1000) as Time, // Convert ms to seconds
      open: d.open,
      high: d.high,
      low: d.low,
      close: d.close,
    }));

    this.candlestickSeries.setData(formattedData);

    // Fit content to show all data
    this.chart.timeScale().fitContent();
  }

  /**
   * Add a single data point (for real-time updates)
   */
  addDataPoint(dataPoint: OHLCData): void {
    const formattedPoint: CandlestickData = {
      time: Math.floor(dataPoint.timestamp / 1000) as Time,
      open: dataPoint.open,
      high: dataPoint.high,
      low: dataPoint.low,
      close: dataPoint.close,
    };

    this.candlestickSeries.update(formattedPoint);
  }

  /**
   * Update series options (e.g., colors)
   */
  updateSeriesOptions(options: Partial<CandlestickSeriesOptions>): void {
    this.candlestickSeries.applyOptions(options);
  }

  /**
   * Resize the chart
   */
  resize(width: number, height: number): void {
    this.chart.applyOptions({
      width,
      height,
    });
  }

  /**
   * Fit content to visible range
   */
  fitContent(): void {
    this.chart.timeScale().fitContent();
  }

  /**
   * Scroll to specific position
   */
  scrollToPosition(position: number, animated: boolean = true): void {
    this.chart.timeScale().scrollToPosition(position, animated);
  }

  /**
   * Get visible range
   */
  getVisibleRange() {
    return this.chart.timeScale().getVisibleRange();
  }

  /**
   * Subscribe to crosshair move events
   */
  subscribeCrosshairMove(handler: (param: any) => void): void {
    this.chart.subscribeCrosshairMove(handler);
  }

  /**
   * Remove the chart
   */
  remove(): void {
    this.chart.remove();
  }

  /**
   * Get the chart API instance
   */
  getChart(): IChartApi {
    return this.chart;
  }

  /**
   * Get the candlestick series
   */
  getSeries(): ISeriesApi<'Candlestick'> {
    return this.candlestickSeries;
  }

  /**
   * Add a line series for indicators (simple approach per TradingView docs)
   */
  addLineSeries(id: string, options?: Partial<LineSeriesOptions>): ISeriesApi<'Line'> {
    if (this.lineSeries.has(id)) {
      return this.lineSeries.get(id)!;
    }

    // Merge options with defaults, allowing all options to pass through
    const seriesOptions: Partial<LineSeriesOptions> = {
      color: '#2962FF',
      lineWidth: 1,
      lineType: 2 as any, // Force lineType to 2 (Curved) - bypass TypeScript
      lineVisible: true, // Ensure line is visible
      lastValueVisible: false,
      priceLineVisible: false,
      ...options // Options will override defaults
    };

    console.log(`[LightweightChart] Adding line series ${id} with options:`, seriesOptions);

    // Try creating series with explicit type
    const series = this.chart.addSeries(LineSeries, seriesOptions);

    console.log(`[LightweightChart] Created line series ${id}`);

    this.lineSeries.set(id, series);
    return series;
  }

  /**
   * Remove a line series
   */
  removeLineSeries(id: string): void {
    const series = this.lineSeries.get(id);
    if (series) {
      this.chart.removeSeries(series);
      this.lineSeries.delete(id);
    }
  }

  /**
   * Get a line series by ID
   */
  getLineSeries(id: string): ISeriesApi<'Line'> | undefined {
    return this.lineSeries.get(id);
  }

  /**
   * Clear all line series
   */
  clearLineSeries(): void {
    this.lineSeries.forEach((series) => {
      this.chart.removeSeries(series);
    });
    this.lineSeries.clear();
  }

  /**
   * Enable smooth animated zoom (replaces the default discrete wheel zoom)
   */
  enableSmoothZoom(): void {
    const timeScale = this.chart.timeScale();
    let targetBarSpacing = timeScale.options().barSpacing;
    let currentBarSpacing = targetBarSpacing;
    let animating = false;

    // Disable built-in wheel zoom
    this.chart.applyOptions({
      handleScale: {
        mouseWheel: false,
      },
    });

    const animate = () => {
      const diff = targetBarSpacing - currentBarSpacing;
      if (Math.abs(diff) < 0.05) {
        currentBarSpacing = targetBarSpacing;
        timeScale.applyOptions({ barSpacing: currentBarSpacing });
        animating = false;
        return;
      }
      // Lerp toward target (0.25 = snappy but smooth)
      currentBarSpacing += diff * 0.25;
      timeScale.applyOptions({ barSpacing: currentBarSpacing });
      requestAnimationFrame(animate);
    };

    this.container.addEventListener('wheel', (e: WheelEvent) => {
      e.preventDefault();

      const zoomFactor = 1 - e.deltaY * 0.002;
      const minSpacing = timeScale.options().minBarSpacing || 1;
      const maxSpacing = 50;

      // Sync current state in case user dragged the axis manually
      currentBarSpacing = timeScale.options().barSpacing;
      targetBarSpacing = Math.max(minSpacing, Math.min(maxSpacing, currentBarSpacing * zoomFactor));

      if (!animating) {
        animating = true;
        requestAnimationFrame(animate);
      }
    }, { passive: false });
  }

  /**
   * Setup resize handler
   */
  private setupResizeHandler(): void {
    const resizeObserver = new ResizeObserver(entries => {
      if (entries.length === 0 || !entries[0].target) {
        return;
      }

      const { width, height } = entries[0].contentRect;
      this.resize(width, height);
    });

    resizeObserver.observe(this.container);
  }
}
